export * from "ce/api/UserApi";
import { UserApi as CE_UserApi } from "ce/api/UserApi";
import type { AxiosPromise } from "axios";
import Api from "api/Api";
import type { ApiResponse } from "api/ApiResponses";
export interface FetchSamlMetadataPayload {
  isEnabled: boolean;
  importFromUrl?: string;
  importFromXml?: string;
  configuration?: {
    singleSignOnServiceUrl: string;
    signingCertificate: string;
    emailField: string;
  };
  // TODO: Fix this the next time the file is edited
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  claims?: Record<string, any>;
}

export type CheckpointAcknowledgeMode = "COMPLETE" | "SKIP";

export type CheckpointAwardMethod =
  | "ACK"
  | "ACK_OVERRIDE"
  | "MIGRATION"
  | "AUTO_AWARDED_INAPPLICABLE";

export interface AdminCentralItem {
  id: string;
  title: string;
  subtitle: string;
  details: Array<{
    label: string;
    value: string;
  }>;
  documentationUrl?: string;
  acknowledgeVersion?: string;
  name?: string;
  next?: boolean;
  status?: string;
  statusMessage?: string;
  statusDetail?: string;
  table?: {
    columns: string[];
    rows: string[][];
  };
  acknowledgeMode?: CheckpointAcknowledgeMode;
  acknowledgeConfirmation?: string;
}

export interface AdminCentralEntry {
  id: string;
  severity: "info" | "warning" | "error";
  title: string;
  description: string;
  status: string;
  details: Array<{
    label: string;
    value: string;
  }>;
  items: AdminCentralItem[];
  itemsLabel?: string;
  documentationUrl?: string;
}

export interface CheckpointView {
  version: string;
  title?: string;
  description?: string;
  docsUrl?: string;
  collectedAt?: string | number;
  collectedBy?: string;
  awardMethod?: CheckpointAwardMethod;
}

export interface CheckpointsResponse {
  collected: CheckpointView[];
  pending: CheckpointView[];
}

export interface InstanceInfo {
  version: string;
  commitSha?: string;
  commitUrl?: string;
  githubRef?: string;
  githubRunUrl?: string;
  imageBuiltAt?: string;
  isCI?: boolean;
}

class UserApi extends CE_UserApi {
  static fetchSamlMetadataURL = "/v1/admin/sso/saml";
  static adminCentralURL = "v1/admin/central";
  static checkpointsURL = "v1/admin/checkpoints";
  static instanceInfoURL = "/info";

  static async fetchSamlMetadata(
    payload: FetchSamlMetadataPayload,
  ): Promise<AxiosPromise<ApiResponse>> {
    return Api.put(UserApi.fetchSamlMetadataURL, payload);
  }

  static async fetchAdminCentral(): Promise<
    AxiosPromise<ApiResponse<AdminCentralEntry[]>>
  > {
    return Api.get(UserApi.adminCentralURL);
  }

  static checkpointAckURL(version: string) {
    return `v1/admin/checkpoints/${encodeURIComponent(version)}/ack`;
  }

  static async fetchCheckpoints(): Promise<
    AxiosPromise<ApiResponse<CheckpointsResponse>>
  > {
    return Api.get(UserApi.checkpointsURL);
  }

  static async fetchInstanceInfo(): Promise<InstanceInfo> {
    const response = await fetch(UserApi.instanceInfoURL);

    if (!response.ok) {
      throw new Error(String(response.status));
    }

    return response.json() as Promise<InstanceInfo>;
  }

  static async acknowledgeCheckpoint(
    version: string,
    mode: CheckpointAcknowledgeMode,
  ): Promise<AxiosPromise<ApiResponse>> {
    return Api.post(UserApi.checkpointAckURL(version), { mode });
  }
}

export default UserApi;
